<script setup>
import { ref, onMounted, onBeforeUnmount, defineAsyncComponent, watch } from 'vue'
import { usePortAllData } from '@/stores/portAllData'
import { storeToRefs } from 'pinia'
import { ULH_MESSAGE_RECEIVED_EVENT_TYPE, DBMANAGER_MESSAGE_RECEIVED_EVENT_TYPE, DELETE_TILE_CACHE_EVENT } from '@/plugins/utils/events.js'
import { round } from '@/plugins/utils/helpers.js';

const AdminModule = defineAsyncComponent(() => import('./modules/admin/AdminModule.vue'))
const ProfileModule = defineAsyncComponent(() => import('./modules/profile/ProfileModule.vue'))
const LayerSwitcher = defineAsyncComponent(() => import('./modules/layerSwitcher/LayerSwitcher.vue'))
const GetAlert = defineAsyncComponent(() => import('./modules/getAlert/GetAlert.vue'))
const GetFeatureWfs = defineAsyncComponent(() => import('./modules/getFeatureWfs/GetFeatureWfs.vue'))
const EditFeature = defineAsyncComponent(() => import('./modules/editFeature/EditFeature.vue'))
const EditMeasure = defineAsyncComponent(() => import('./modules/editMeasure/EditMeasure.vue'))
const EditNotice = defineAsyncComponent(() => import('./modules/editNotice/EditNotice.vue'))
const EditMarinerNote = defineAsyncComponent(() => import('./modules/editMarinerNote/EditMarinerNote.vue'))
const EditObstruction = defineAsyncComponent(() => import('./modules/editObstruction/EditObstruction.vue'))
const EditIssue = defineAsyncComponent(() => import('./modules/editIssue/EditIssue.vue'))
const ShipPlacement = defineAsyncComponent(() => import('./modules/shipPlacement/ShipPlacement.vue'))
const PassagePlan = defineAsyncComponent(() => import('./modules/passagePlan/PassagePlan.vue'))
const AisMainView = defineAsyncComponent(() => import('./modules/ais/AisMainView.vue'));
const ReplayMain = defineAsyncComponent(() => import('./modules/replay/ReplayMain.vue'))
const WayEditor = defineAsyncComponent(() => import('./modules/wayEditor/WayEditor.vue'))
const UlhyssesMainView = defineAsyncComponent(() => import ('./modules/ulhysses/UlhyssesMainView.vue'))
const NotifMainView = defineAsyncComponent(() => import('./modules/notification/NotifMainView.vue'))
const DbManager = defineAsyncComponent(() => import('./modules/dbManager/DbManager.vue'))

const store = usePortAllData()
const { setModuleProcessing, addUlhMessage } = store
const { getModuleProcessing, getIsMobile } = storeToRefs(store)

const modulePalette = ref(null)

const ulhBuildLabel = ref('')
const ulhBuildStep = ref('')
const dbmMessage = ref('')

const props = defineProps({
  moduleParam: {
    type: Object,
    default: () => {
      return {}
    }
  }
})

onMounted(() => {
  document.addEventListener(ULH_MESSAGE_RECEIVED_EVENT_TYPE, eventForUlh)
  document.addEventListener(DBMANAGER_MESSAGE_RECEIVED_EVENT_TYPE, eventForDbm)
})

onBeforeUnmount(() => {
  document.removeEventListener(ULH_MESSAGE_RECEIVED_EVENT_TYPE, eventForUlh)
  document.removeEventListener(DBMANAGER_MESSAGE_RECEIVED_EVENT_TYPE, eventForDbm)
})

const eventForUlh = (e) => {
  const message = e.detail
  if (message.isWorking === false || message.isWorking === true) {
    setModuleProcessing(115, message.isWorking)
    addUlhMessage(JSON.stringify({ isWorking: message.isWorking }))
  } else {
    addUlhMessage(message.message)
    let progressMessage
    if (message.message[0] === '{') progressMessage = JSON.parse(message.message)
    else progressMessage = message.message
    if (progressMessage.step) {
      ulhBuildLabel.value = progressMessage.msg
      ulhBuildStep.value = `${round(progressMessage.step * 100, 1)}%`
    } else if (typeof progressMessage === 'string') {
      ulhBuildLabel.value = progressMessage
      ulhBuildStep.value = ''
    } else {
      ulhBuildLabel.value = ''
      ulhBuildStep.value = ''
    }
  }
}

const eventForDbm = (e) => {
  const message = e.detail
  if (message.isWorking === false || message.isWorking === true) {
    setModuleProcessing(121, message.isWorking)
  } else {
    dbmMessage.value = JSON.stringify(message);
  }
  if (message.type === 'REFRESH_PRODUCTION_DATABASE') {
    document.dispatchEvent(new CustomEvent(DELETE_TILE_CACHE_EVENT))
  }
}

const scrollTop = () => {
  modulePalette.value.scrollTo({
    top: 0,
    left: 0,
    behavior: "smooth"
  })
}

watch(() => props.moduleParam, (nv) => {
  if (nv && nv.module && getIsMobile.value) document.dispatchEvent(new CustomEvent('closeFI'))
}, { deep: true })
</script>

<template>
  <div ref="modulePalette" class="geomod-module-palette">
    <admin-module v-if="moduleParam.module === 'admin'" />
    <profile-module v-if="moduleParam.module === 'profile'" />
    <layer-switcher v-if="moduleParam.module === 'layerSwitcher'" />
    <get-alert v-if="moduleParam.module === 'getAlert'" />
    <get-feature-wfs v-if="moduleParam.module === 'getFeature'" />
    <edit-feature v-if="moduleParam.module === 'editFeature'" />
    <edit-measure v-if="moduleParam.module === 'editMeasure'" />
    <edit-notice v-if="moduleParam.module === 'editNotice'" />
    <edit-mariner-note v-if="moduleParam.module === 'editMariners'" />
    <edit-obstruction v-if="moduleParam.module === 'editObstruction'" />
    <edit-issue v-if="moduleParam.module === 'editIssue'" />
    <ship-placement v-if="moduleParam.module === 'shipPlacement'" @scroll-top="scrollTop" />
    <passage-plan v-if="moduleParam.module === 'passagePlan'" />
    <replay-main v-if="moduleParam.module === 'replay'" />
    <way-editor v-if="moduleParam.module === 'wayEditor'" />
    <ulhysses-main-view v-if="moduleParam.module === 'ulhysses'" :is-working="getModuleProcessing[moduleParam.code]" :build-label="ulhBuildLabel" :build-step="ulhBuildStep" />
    <ais-main-view v-if="moduleParam.module === 'ais'" />
    <notif-main-view v-if="moduleParam.module === 'notification'" />
    <db-manager v-if="moduleParam.module === 'dbmanager'" :is-working="getModuleProcessing[moduleParam.code]" :dbm-message="dbmMessage" />
  </div>
</template>